import React, { memo, useState, useRef } from 'react';
import { Heading } from './Heading.js';
import { Paragraph } from './Paragraph.js';
import caret from './packages/components/assets/images/caret.svg.js';

const CollapsibleIcon = ({ open, variant }) => {
    switch (variant) {
        case 'primary':
        case 'border':
        case 'minimal':
            return (React.createElement("img", { src: caret, className: ['transition-all h-[6px]', open ? 'rotate-180' : ''].join(' ') }));
        default:
            return open ? React.createElement("span", { className: "-mt-1" }, "\u2014") : React.createElement("span", null, "+");
    }
};
const getStyles = ({ titleBold, titleLarge }) => ({
    button: {
        boxed: 'text-xl text-green',
        gradient: 'border w-10 h-10 text-3xl leading-10 text-black border-lilac-200 group-hover:bg-lilac-200',
        border: 'border border-lilac-600 w-6 h-6 group-hover:bg-lilac-100',
        minimal: '',
        primary: '',
    },
    content: {
        boxed: 'p-6 pt-0',
        gradient: 'p-6',
        border: 'pb-5 px-4 sm:px-6 text-gray-800',
        minimal: 'pt-3 pb-4',
        primary: 'pt-2',
    },
    title: {
        boxed: 'p-6',
        gradient: 'p-6 font-sans rounded-2xl bg-gradient-to-r from-lilac-50 via-green-200 to-lilac-50',
        border: [
            'py-4 px-4 sm:px-6 sm:text-lg',
            titleBold ? 'font-medium' : '',
        ].join(' '),
        minimal: [
            'font-sans pb-6 pr-2',
            titleLarge ? 'text-3xl sm:text-5xl leading-tight' : '',
            titleBold ? 'font-medium' : '',
        ].join(' '),
        primary: [
            'font-sans',
            titleLarge ? 'text-3xl sm:text-5xl leading-tight' : '',
            titleBold ? 'font-medium' : '',
        ].join(' '),
    },
    wrap: {
        boxed: 'rounded-2xl bg-gray-400 bg-opacity-20',
        gradient: '',
        border: 'border border-indigo-200 rounded-lg',
        minimal: 'border-b border-[#DEE0DE]',
        primary: '',
    },
    excerpt: {
        boxed: 'pb-6 px-6',
        gradient: 'pt-6',
        border: 'pb-3 px-6',
        minimal: 'pt-3 pb-4',
        primary: '',
    },
});
const Collapsible = memo(({ buttonAlign = 'end', children, className = '', excerpt, scroller = false, title, subtitle, variant = 'boxed', openCollapsible = false, titleLarge = false, titleBold = false, titleTagName = 'h3', ...props }) => {
    const [open, setOpen] = useState(openCollapsible);
    const contentEl = useRef(null);
    const excerptEl = useRef(null);
    const summaryEl = useRef(null);
    const styles = getStyles({ titleBold, titleLarge });
    return (React.createElement("details", { className: [
            'overflow-hidden transition-all duration-300 ease-linear',
            styles.wrap[variant],
            className,
        ].join(' '), open: open, onToggle: e => {
            const state = e.target.open;
            setOpen(state);
        }, ...props },
        React.createElement("summary", { ref: summaryEl, className: "list-none outline-none cursor-pointer group" },
            React.createElement("span", { className: [
                    'grid grid-cols-12 grid-flow-col items-center pointer-events-none text-black leafwell-collapsible',
                    buttonAlign === 'start' ? 'justify-start' : 'justify-between',
                    styles.title[variant],
                ].join(' ') },
                React.createElement("span", { className: "col-span-11" },
                    React.createElement(Heading, { level: variant === 'gradient' ? '4' : '6', tagName: titleTagName, className: [
                            'grid grid-flow-col items-center pointer-events-none text-black leafwell-collapsible',
                            buttonAlign === 'start' ? 'justify-start' : 'justify-between',
                            'font-sans',
                        ].join(' ') }, title),
                    subtitle ? (React.createElement("p", { className: "font-sans prose text-sm text-nowrap" }, subtitle)) : null),
                React.createElement("span", { className: [
                        'transition-all ml-3 rounded-full grid justify-items-center items-center justify-self-end',
                        styles.button[variant],
                    ].join(' '), "aria-hidden": "true" },
                    React.createElement(CollapsibleIcon, { ...{ open, variant } }))),
            excerpt && (React.createElement("div", { ref: excerptEl, "aria-hidden": "true", className: [
                    'transition-opacity duration-200 ease-linear',
                    open ? 'invisible absolute opacity-0' : 'delay-300',
                    styles.excerpt[variant],
                ].join(' ') },
                React.createElement(Paragraph, { size: "small" }, excerpt)))),
        React.createElement("div", { ref: contentEl, className: [
                'transition-opacity duration-200 ease-linear',
                styles.content[variant],
                open ? 'delay-300' : 'opacity-0',
                scroller ? 'max-h-52 overflow-auto pr-3' : '',
            ].join(' ') }, children)));
});

export { Collapsible };
//# sourceMappingURL=Collapsible.js.map
